import {
  DriveAuthError,
  ensureGoogleAccessToken,
  requestAccessTokenSilent,
  clearToken,
  GOOGLE_DRIVE_SCOPES,
} from "@/lib/googleAuth";
import { isTextBasedDocument } from "@/lib/isTextBasedDocument";

export type DriveEntry = {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  parents?: string[];
  webViewLink?: string;
  isFolder: boolean;
};

export type DriveFolderContents = {
  folders: DriveEntry[];
  files: DriveEntry[];
};

export type DriveImportCandidate = DriveEntry & {
  drivePath?: string;
};

const FOLDER_MIME = "application/vnd.google-apps.folder";

const ATTACHABLE_GOOGLE_APP_MIMES = new Set([
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.drawing",
]);

/** Files that can be attached in chat (images, media, PDFs, docs). */
export function isAttachableDriveFile(mimeType: string, name: string): boolean {
  if (mimeType === FOLDER_MIME) return false;
  if (isTextBasedDocument(mimeType, name)) return true;
  if (
    mimeType.startsWith("image/") ||
    mimeType.startsWith("audio/") ||
    mimeType.startsWith("video/")
  ) {
    return true;
  }
  if (mimeType === "application/pdf") return true;
  return ATTACHABLE_GOOGLE_APP_MIMES.has(mimeType);
}

function mapApiFile(file: Record<string, unknown>): DriveEntry {
  const mimeType = String(file.mimeType || "");
  return {
    id: String(file.id),
    name: String(file.name || "Untitled"),
    mimeType,
    modifiedTime: String(file.modifiedTime || ""),
    parents: Array.isArray(file.parents) ? (file.parents as string[]) : undefined,
    webViewLink: file.webViewLink ? String(file.webViewLink) : undefined,
    isFolder: mimeType === FOLDER_MIME,
  };
}

async function parseDriveError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const message = body?.error?.message;
    if (typeof message === "string" && message.trim()) return message;
  } catch {
    /* ignore */
  }
  return response.statusText || `HTTP ${response.status}`;
}

async function driveFetch(
  url: string,
  token: string,
  retried = false
): Promise<Response> {
  let accessToken = await ensureGoogleAccessToken(token, {
    refreshScope: GOOGLE_DRIVE_SCOPES,
    requiredScope: "https://www.googleapis.com/auth/drive.readonly",
  });

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (response.status === 401 || response.status === 403) {
    if (!retried) {
      clearToken();
      const refreshed = await requestAccessTokenSilent(GOOGLE_DRIVE_SCOPES);
      if (refreshed) {
        return driveFetch(url, refreshed, true);
      }
    }
    throw new DriveAuthError();
  }

  if (!response.ok) {
    const detail = await parseDriveError(response);
    throw new Error(`Failed to list folder contents: ${detail}`);
  }

  return response;
}

export async function listFolderChildren(
  folderId: string,
  token: string
): Promise<DriveFolderContents> {
  const parentId = folderId === "root" ? "root" : folderId;
  const folders: DriveEntry[] = [];
  const files: DriveEntry[] = [];
  let pageToken: string | undefined;

  do {
    const params = new URLSearchParams({
      q: `'${parentId}' in parents and trashed = false`,
      fields:
        "nextPageToken, files(id, name, mimeType, modifiedTime, parents, webViewLink)",
      pageSize: "100",
      orderBy: "folder,name",
    });
    if (pageToken) params.append("pageToken", pageToken);

    const response = await driveFetch(
      `https://www.googleapis.com/drive/v3/files?${params.toString()}`,
      token
    );

    const data = await response.json();
    for (const raw of data.files || []) {
      const entry = mapApiFile(raw);
      if (entry.isFolder) {
        folders.push(entry);
      } else if (isTextBasedDocument(entry.mimeType, entry.name)) {
        files.push(entry);
      }
    }
    pageToken = data.nextPageToken;
  } while (pageToken);

  return { folders, files };
}

/** List folders and attachable files for chat / attachment flows. */
export async function listAttachableFolderChildren(
  folderId: string,
  token: string
): Promise<DriveFolderContents> {
  const parentId = folderId === "root" ? "root" : folderId;
  const folders: DriveEntry[] = [];
  const files: DriveEntry[] = [];
  let pageToken: string | undefined;

  do {
    const params = new URLSearchParams({
      q: `'${parentId}' in parents and trashed = false`,
      fields:
        "nextPageToken, files(id, name, mimeType, modifiedTime, parents, webViewLink)",
      pageSize: "100",
      orderBy: "folder,name",
    });
    if (pageToken) params.append("pageToken", pageToken);

    const response = await driveFetch(
      `https://www.googleapis.com/drive/v3/files?${params.toString()}`,
      token
    );

    const data = await response.json();
    for (const raw of data.files || []) {
      const entry = mapApiFile(raw);
      if (entry.isFolder) {
        folders.push(entry);
      } else if (isAttachableDriveFile(entry.mimeType, entry.name)) {
        files.push(entry);
      }
    }
    pageToken = data.nextPageToken;
  } while (pageToken);

  return { folders, files };
}

async function collectFromFolderRecursive(
  folderId: string,
  drivePathForFilesHere: string,
  token: string,
  onScanProgress?: (message: string) => void
): Promise<DriveImportCandidate[]> {
  const results: DriveImportCandidate[] = [];
  const { folders, files } = await listFolderChildren(folderId, token);

  for (const file of files) {
    results.push({
      ...file,
      drivePath: drivePathForFilesHere || undefined,
    });
  }

  for (const folder of folders) {
    const subPath = drivePathForFilesHere
      ? `${drivePathForFilesHere}/${folder.name}`
      : folder.name;
    onScanProgress?.(`Scanning ${subPath}…`);
    const nested = await collectFromFolderRecursive(
      folder.id,
      subPath,
      token,
      onScanProgress
    );
    results.push(...nested);
  }

  return results;
}

/** Recursively collect importable files from the given Drive folders. */
export async function collectFilesFromFolders(
  folders: DriveEntry[],
  token: string,
  parentDrivePath: string,
  onScanProgress?: (message: string) => void
): Promise<DriveImportCandidate[]> {
  const all: DriveImportCandidate[] = [];

  for (const folder of folders) {
    const folderDrivePath = parentDrivePath
      ? `${parentDrivePath}/${folder.name}`
      : folder.name;
    onScanProgress?.(`Scanning ${folderDrivePath}…`);
    const nested = await collectFromFolderRecursive(
      folder.id,
      folderDrivePath,
      token,
      onScanProgress
    );
    all.push(...nested);
  }

  return all;
}

export async function searchDriveFiles(
  query: string,
  token: string
): Promise<DriveEntry[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const q = `trashed = false and name contains '${trimmed.replace(/'/g, "\\'")}'`;
  const params = new URLSearchParams({
    q,
    fields: "files(id, name, mimeType, modifiedTime, parents, webViewLink)",
    pageSize: "50",
    orderBy: "folder,name",
  });

  const response = await driveFetch(
    `https://www.googleapis.com/drive/v3/files?${params.toString()}`,
    token
  );

  const data = await response.json();
  const results: DriveEntry[] = [];
  for (const raw of data.files || []) {
    const entry = mapApiFile(raw);
    if (entry.isFolder || isTextBasedDocument(entry.mimeType, entry.name)) {
      results.push(entry);
    }
  }
  return results;
}

export async function getDriveFileBlob(
  file: Pick<DriveEntry, "id" | "mimeType" | "name">,
  token: string,
  forTextExtraction = true
): Promise<Blob> {
  const googleWorkspaceMimeTypes: Record<string, string> = {
    "application/vnd.google-apps.document": forTextExtraction
      ? "text/plain"
      : "application/pdf",
    "application/vnd.google-apps.spreadsheet": forTextExtraction
      ? "text/csv"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.google-apps.presentation": forTextExtraction
      ? "text/plain"
      : "application/pdf",
  };

  let url: string;
  if (file.mimeType.startsWith("application/vnd.google-apps.")) {
    const exportMimeType =
      googleWorkspaceMimeTypes[file.mimeType] ||
      (forTextExtraction ? "text/plain" : "application/pdf");
    url = `https://www.googleapis.com/drive/v3/files/${file.id}/export?mimeType=${encodeURIComponent(exportMimeType)}`;
  } else {
    url = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`;
  }

  const response = await driveFetch(url, token);

  return response.blob();
}

export { DriveAuthError };
